import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MatchRequest, Page, PageRequest } from "../api";

/** Rows per page when a screen does not say otherwise. */
export const DEFAULT_PAGE_SIZE = 100;

/** How long typing settles before the search goes to the server. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * What a grid is currently asking for, in the server's vocabulary.
 *
 * The names are field names, not column labels, and `DataTable` builds them
 * from its column ids — which is the whole contract between the two sides: a
 * column named `code` sorts and filters on the repository's `code`, or the
 * request is rejected.
 */
export interface TableQuery {
  /** `["code,asc"]`, applied in order. */
  sort: string[];
  /** Field name to its values; several mean "any of", two mean a range. */
  filters: Record<string, string[]>;
  /** The free-text search box. */
  q: string;
}

export const EMPTY_TABLE_QUERY: TableQuery = { sort: [], filters: {}, q: "" };

/**
 * Everything `DataTable` needs to stop being the source of truth about its own
 * rows. Handed to it as the `server` prop; build one with {@link useServerTable}.
 */
export interface ServerTableMode {
  /** Rows in the unfiltered set — the "Total" badge. */
  total: number;
  /** Rows matching the current filters — the "Filtered" badge. */
  matching: number;
  /** Whether another page is available. */
  hasMore: boolean;
  /** Whether one is on its way, so the grid can say so rather than look finished. */
  loadingMore: boolean;
  /** Called as the scroll approaches the end of what is loaded. */
  loadMore: () => void;
  /** Called whenever the user changes the sort, a filter, or the search. */
  onQueryChange: (query: TableQuery) => void;
  /**
   * Resolves the ids of every row matching the current query, loaded or not.
   *
   * Omit it and the header checkbox selects the loaded rows only. Provide it
   * and it means what it has always meant on these screens: everything the
   * filters match.
   */
  selectAll?: () => Promise<string[]>;
}

export interface ServerTableOptions<T> {
  /**
   * Cache key for this resource — `["artwork", "lazy"]`. The live query is
   * appended to it, so invalidating the prefix refreshes every page of every
   * filter combination at once.
   */
  queryKey: readonly unknown[];
  /** One page. Normally `client.find`. */
  fetchPage: (request: PageRequest) => Promise<Page<T>>;
  /** Every matching id. Normally `client.findMatchingIds`; omit to disable select-all. */
  fetchMatchingIds?: (request: MatchRequest) => Promise<Array<string | number>>;
  /**
   * The unfiltered row count, for the "Total" badge — normally `client.count`.
   *
   * Separate from the page because it answers a different question: a page
   * knows how many rows match the filters, not how many exist. Fetched once
   * and held, since it only moves when a row is created or deleted.
   */
  fetchTotal?: () => Promise<number>;
  pageSize?: number;
  enabled?: boolean;
}

export interface ServerTable<T> {
  /** Every row loaded so far, in order. */
  rows: T[];
  /** The first page is in flight — distinct from loading a later one. */
  loading: boolean;
  error: unknown;
  /** Server-computed totals over the matching rows, for the screen's own badges. */
  aggregates: Record<string, number>;
  /** Rows matching the current filters. */
  matching: number;
  /** Hand this to `<DataTable server={...} />`. */
  mode: ServerTableMode;
  /** Re-reads every page of the current query — what to call after a mutation. */
  refresh: () => Promise<void>;
}

/**
 * Drives a `DataTable` from a server-paged endpoint: the grid shows one page,
 * loads the next as the user scrolls, and hands sorting, filtering and search
 * to the database instead of doing them over rows it happens to hold.
 *
 * ```tsx
 * const artworks = useServerTable({
 *   queryKey: ["artwork", "lazy"],
 *   fetchPage: (request) => api.lazyArtworks.find(request),
 *   fetchMatchingIds: (request) => api.lazyArtworks.findMatchingIds(request),
 *   fetchTotal: () => api.lazyArtworks.count(),
 * });
 *
 * <DataTable data={artworks.rows} server={artworks.mode} loading={artworks.loading} … />
 * ```
 *
 * Three things follow from the rows no longer all being in the browser, and
 * the grid handles each rather than pretending otherwise: the badges come from
 * the server, select-all resolves ids server-side, and the filter dropdowns
 * must declare their options (`meta.filterOptions`) because the loaded page is
 * no longer a complete sample of the values.
 */
export function useServerTable<T>(options: ServerTableOptions<T>): ServerTable<T> {
  const { queryKey, pageSize = DEFAULT_PAGE_SIZE, enabled = true } = options;
  const client = useQueryClient();

  // Held in refs so a screen can pass inline arrows — which it always will —
  // without every render invalidating the query or rebuilding the mode object.
  const fetchPage = useLatest(options.fetchPage);
  const fetchMatchingIds = useLatest(options.fetchMatchingIds);
  const fetchTotal = useLatest(options.fetchTotal);

  const [applied, setApplied] = useState<TableQuery>(EMPTY_TABLE_QUERY);
  const appliedRef = useRef(applied);
  appliedRef.current = applied;

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current !== null) clearTimeout(timer.current);
  }, []);

  /**
   * Sorting and filters go to the server as they happen; only typing waits,
   * because a round trip per keystroke would make the box unusable on a slow
   * link and pointless work on a fast one.
   */
  const onQueryChange = useCallback((next: TableQuery) => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const apply = () =>
      setApplied((current) => (tokenOf(current) === tokenOf(next) ? current : next));

    if (next.q === appliedRef.current.q) {
      apply();
      return;
    }
    timer.current = setTimeout(() => {
      timer.current = null;
      apply();
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const token = tokenOf(applied);

  const pages = useInfiniteQuery({
    queryKey: [...queryKey, "page", token, pageSize],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchPage.current({
        page: pageParam,
        size: pageSize,
        sort: applied.sort,
        filters: applied.filters,
        q: applied.q,
        // The count and the aggregates describe the whole query rather than
        // this slice of it, so they are paid for once, with the first page,
        // and reused for every page after.
        total: pageParam === 0,
      }),
    getNextPageParam: (last, all) => {
      const matching = all[0]?.total;
      if (typeof matching === "number") {
        const loaded = all.reduce((count, page) => count + page.content.length, 0);
        return loaded < matching ? last.page + 1 : undefined;
      }
      // No count to go by: stop at the first short page.
      return last.content.length < pageSize ? undefined : last.page + 1;
    },
    enabled,
    staleTime: 60_000,
    // A changed filter must not blank the grid: without this the new query is
    // "pending", the table shows a spinner where its own filter row was, and
    // the user loses the control they just touched. The previous rows stay up,
    // with their own counts, until the new ones land — so what is on screen is
    // always one coherent answer, just occasionally the previous question's.
    placeholderData: keepPreviousData,
  });

  const total = useQuery({
    queryKey: [...queryKey, "total"],
    queryFn: () => fetchTotal.current!(),
    enabled: enabled && Boolean(options.fetchTotal),
    staleTime: 5 * 60_000,
  });

  const rows = useMemo(
    () => pages.data?.pages.flatMap((page) => page.content) ?? [],
    [pages.data],
  );

  const first = pages.data?.pages[0];
  const matching = first?.total ?? rows.length;
  const aggregates = first?.aggregates ?? EMPTY_AGGREGATES;

  // Showing the previous query's rows while the new one loads. Nothing may ask
  // for "the next page" here: it would be the next page of the *new* query,
  // landing after page 0 that has not arrived, with a hole where the rows
  // between them should be.
  const stale = pages.isPlaceholderData;

  const key = useLatest(queryKey);
  const refresh = useCallback(async () => {
    await client.invalidateQueries({ queryKey: [...key.current] });
  }, [client, key]);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = pages;
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const canSelectAll = Boolean(options.fetchMatchingIds);
  const selectAll = useCallback(async () => {
    const { sort, filters, q } = appliedRef.current;
    const ids = await fetchMatchingIds.current!({ sort, filters, q });
    return ids.map(String);
  }, []);

  const mode = useMemo<ServerTableMode>(
    () => ({
      // Before the count arrives, the matching rows are the best answer there
      // is — better than a badge that reads 0 for a moment on every load.
      total: total.data ?? matching,
      matching,
      hasMore: hasNextPage && !stale,
      // A refresh of the whole query reads as "more is coming" too, which is
      // what the grid's trailing spinner says.
      loadingMore: isFetchingNextPage || stale,
      loadMore,
      onQueryChange,
      ...(canSelectAll ? { selectAll } : {}),
    }),
    [total.data, matching, hasNextPage, isFetchingNextPage, stale, loadMore, onQueryChange, canSelectAll, selectAll],
  );

  return {
    rows,
    loading: pages.isLoading,
    error: pages.error,
    aggregates,
    matching,
    mode,
    refresh,
  };
}

const EMPTY_AGGREGATES: Record<string, number> = {};

/** Keeps the newest value of a callback without making it a render dependency. */
function useLatest<F>(value: F): { current: F } {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/**
 * A canonical string for a query — its cache key, and the test for "did this
 * actually change". Filter names are sorted so that two states differing only
 * in the order the user touched the controls are one query, not two.
 */
function tokenOf(query: TableQuery): string {
  const filters = Object.keys(query.filters)
    .sort()
    .map((name) => [name, query.filters[name] ?? []] as const);
  return JSON.stringify([query.sort, filters, query.q]);
}
